/**
 * Small presentational pieces shared by the two Carbon drift tables and the
 * drift detail (mockup 02), built from the Carbon primitives:
 *   ChangeCell      the baseline value tag, an arrow, the drifted value as the
 *                   high-contrast tag (the Neutral Bad State Rule)
 *   WhenCell        the detection time, with the row's status as a tag
 *   ValueView       one setting value: a 240px tag, or a code block when the
 *                   value is long or structured
 *   GuardiansRead   the layer-01 "Guardian's read" block led by the iuvo mark
 *   RowToggleTitle  a row's title that also toggles the row's in-place detail
 */

import type { ReactNode } from 'react'
import clsx from 'clsx'
import { CodeSnippet } from '@/components/carbon/CodeSnippet'
import { IconArrowRight } from '@/components/carbon/icons'
import { IuvoMark } from '@/components/carbon/IuvoMark'
import { Tag } from '@/components/carbon/Tag'
import { formatDriftWhen, isLongValue } from './drift-carbon-helpers'

/** A setting value: the default tag (baseline) or the high-contrast tag (now). */
export function ValueView({ value, now = false }: { value: string | null | undefined; now?: boolean }) {
  const text = value == null || value === '' ? '(none)' : value
  if (isLongValue(text)) {
    return <CodeSnippet kind="multi" code={text} maxHeight="8rem" copyLabel={now ? 'Copy the current value' : 'Copy the baseline value'} />
  }
  return (
    <span className="flex min-w-0 max-w-[240px]">
      {now ? (
        <Tag kind="high-contrast" title={text}>
          {text}
        </Tag>
      ) : (
        <Tag title={text} className="text-text-secondary">
          {text}
        </Tag>
      )}
    </span>
  )
}

// Tag carries its own max-w-full, which outranks a max-w class passed to it, so
// the cap goes on a wrapper the tag then fills (and truncates inside). 180px
// keeps two long values from squeezing the Setting column below its 34%.
const CHANGE_VALUE_CAP = 'flex min-w-0 max-w-[180px] max-c-lg:max-w-[120px]'

/** The Change column: baseline → now, each truncating at 180px (120px below c-lg) with the full value on hover. */
export function ChangeCell({ from, to }: { from: string | null | undefined; to: string | null | undefined }) {
  const before = from == null || from === '' ? '(none)' : from
  const after = to == null || to === '' ? '(none)' : to
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className={CHANGE_VALUE_CAP}>
        <Tag title={before} className="text-text-secondary">
          {before}
        </Tag>
      </span>
      <IconArrowRight size={14} className="flex-none text-[color:var(--icon-secondary)]" />
      <span className={CHANGE_VALUE_CAP}>
        <Tag kind="high-contrast" title={after}>
          {after}
        </Tag>
      </span>
    </span>
  )
}

/**
 * The When column (mockup 02), which also carries the row's status: the
 * detection time, led by a default tag for a lifecycle state other than the
 * view's own ("Pending" in the open queue). The tables have no Status column.
 */
export function WhenCell({ at, status, statusTitle }: { at: string | null | undefined; status?: string | null; statusTitle?: string }) {
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      {status ? <Tag title={statusTitle}>{status}</Tag> : null}
      <span className="tabular-nums">{formatDriftWhen(at)}</span>
    </span>
  )
}

/** "Guardian's read": Guardian's recommendation for a drift, on layer-01 inside the layer-02 expanded row. */
export function GuardiansRead({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={clsx('bg-layer-1 p-4 text-body text-text-secondary', className)}>
      <b className="mb-2 flex items-center gap-2 font-semibold text-text-primary">
        <IuvoMark size={14} />
        Guardian&apos;s read
      </b>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  )
}

/**
 * A row title that toggles the row's in-place detail, as a click on the
 * title of a Carbon expandable row does. The table keeps the expansion state
 * (DataTable `expandable.expandedIds`), and `onToggle` flips this row in it.
 * It stays out of the tab order: keyboard users already have the row's
 * chevron. Without `onToggle` the title is plain text.
 */
export function RowToggleTitle({ children, onToggle }: { children: ReactNode; onToggle?: () => void }) {
  if (!onToggle) return <>{children}</>
  return (
    <span
      role="button"
      tabIndex={-1}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onToggle()
        }
      }}
      className="cursor-pointer"
    >
      {children}
    </span>
  )
}
